"use client";

import React, { useState } from "react";
import NextLink from "next/link";
import { useTranslations } from "next-intl";
import { Box, Button, IconButton, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { artworkImageUrl } from "./useArtwork";
import { artworkAspect, localized, type Artwork } from "./artwork-types";
import { cardBadges } from "./artwork-store";
import CardRoomPreview from "./CardRoomPreview";

/** What a list hands each card besides the work itself. */
export interface ArtworkCardLinks {
  href: string;
  locale: string;
  /**
   * One line per material: its name and "od 19,90 €", already formatted.
   * Null while the formats load, so no number flickers.
   */
  offers?: { label: string; price: string }[] | null;
  /** "30 × 40 do 60 × 80 cm": the sizes this work can be printed at. */
  sizeLabel?: string;
  /** Opens the quick-add sheet. Without it the card has no basket button. */
  onQuickAdd?: (artwork: Artwork) => void;
  /** Hearted in this browser (see wishlist.ts). */
  saved?: boolean;
  /** Without it the card has no heart. */
  onToggleSaved?: () => void;
  /** The size the room preview hangs on hover; none, no room preview. */
  roomSize?: { widthMm: number; heightMm: number } | null;
}

export interface ArtworkCardProps extends ArtworkCardLinks {
  artwork: Artwork;
}

/** The frame every card shares: portrait 4:5, whatever the work's own shape. */
export const TILE_ASPECT = 4 / 5;
/** The print's margin inside the frame, as fractions of the frame. */
const INSET_X = 0.1;
const INSET_Y = 0.1;
/** Width over height of the space the print may fill. */
const INNER_ASPECT = (TILE_ASPECT * (1 - 2 * INSET_X)) / (1 - 2 * INSET_Y);

/**
 * The frame behind every print: plain white, and the page's own surface in
 * dark mode. A painted "wall" colour read as a fake mock-up; the room photos
 * on hover are where the work is shown on a wall.
 */
export const tileSx = (theme: Theme) => ({
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#FFFFFF",
  border: "1px solid",
  borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(17,29,41,0.06)",
});

/** Hover effects only where there is a pointer that hovers; a tap is not a hover. */
const HOVER = "@media (hover: hover) and (pointer: fine)";

/**
 * One piece in a list: the print on a wall, its label, what it costs from, and
 * a basket button.
 *
 * Every card is the same frame, so titles and prices line up across the grid
 * however wide or tall the paintings are. The work is never cropped to fill
 * it — the frame is a wall, and the whole picture hangs on it with a shadow,
 * which is what tells a customer at a glance that they are browsing prints and
 * not a picture library.
 *
 * The picture and the label are one real link, so it opens in a new tab and
 * is followed by crawlers. The heart and the basket sit beside it, not inside
 * it: a button inside a link is two controls in one place.
 */
export default function ArtworkCard({
  artwork,
  href,
  locale,
  offers,
  sizeLabel,
  onQuickAdd,
  saved = false,
  onToggleSaved,
  roomSize,
}: ArtworkCardProps) {
  const t = useTranslations("art");
  const title = localized(artwork.title, locale);
  const aspect = artworkAspect(artwork);
  // Fitted by its binding side, so a panorama spans the width and a tall
  // portrait the height, both inside the same margin.
  const wide = aspect >= INNER_ASPECT;
  const badges = cardBadges(artwork);
  const thumb = artworkImageUrl(artwork.slug, "thumb", artwork.updatedAt);

  // The room is only drawn once someone with a mouse has rested on the card;
  // a phone never loads it.
  const [hovered, setHovered] = useState(false);
  const [roomMounted, setRoomMounted] = useState(false);
  const onPointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse" || !roomSize) return;
    setRoomMounted(true);
    setHovered(true);
  };

  return (
    <Box
      component="article"
      onPointerEnter={onPointerEnter}
      onPointerLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
        [HOVER]: {
          "&:hover .artPrint": {
            transform: "translateY(-4px)",
            boxShadow:
              "0 22px 36px -16px rgba(20,24,32,.55), 0 3px 8px rgba(20,24,32,.10)",
          },
          "&:hover .artTitle": { textDecoration: "underline", textUnderlineOffset: 3 },
        },
      }}
    >
      <Box
        component={NextLink}
        href={href}
        sx={{
          display: "block",
          color: "inherit",
          textDecoration: "none",
          borderRadius: 1.5,
          "&:focus-visible": { outline: "3px solid", outlineColor: "primary.main", outlineOffset: 3 },
        }}
      >
        <Box
          sx={(theme) => ({
            ...tileSx(theme),
            position: "relative",
            aspectRatio: String(TILE_ASPECT),
            borderRadius: 1.5,
            overflow: "hidden",
          })}
        >
          <Box
            sx={{
              position: "absolute",
              inset: `${INSET_Y * 100}% ${INSET_X * 100}%`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              className="artPrint"
              src={thumb}
              alt={`${title} — ${artwork.artist}`}
              loading="lazy"
              decoding="async"
              sx={{
                display: "block",
                aspectRatio: String(aspect || 1),
                width: wide ? "100%" : "auto",
                height: wide ? "auto" : "100%",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "cover",
                bgcolor: "action.hover",
                boxShadow:
                  "0 14px 26px -14px rgba(20,24,32,.5), 0 2px 5px rgba(20,24,32,.08)",
                transition: "transform .25s ease, box-shadow .25s ease",
              }}
            />
          </Box>
          {roomMounted && roomSize ? (
            <CardRoomPreview
              src={thumb}
              widthMm={roomSize.widthMm}
              heightMm={roomSize.heightMm}
              visible={hovered}
              // Tall works over the bed, where they hang higher; wide ones
              // over the sofa.
              scene={aspect < 0.9 ? "bed" : "sofa"}
            />
          ) : null}
        </Box>

        {/* A wall label: who, then what. Fixed heights, so a long title on
            one card does not push its price below its neighbour's. */}
        <Box sx={{ pt: 1.25 }}>
          <Typography
            variant="caption"
            component="p"
            color="text.secondary"
            noWrap
            sx={{ letterSpacing: ".07em", textTransform: "uppercase", lineHeight: 1.5 }}
          >
            {artwork.artist}
          </Typography>
          <Typography
            className="artTitle"
            sx={{
              mt: 0.25,
              fontSize: { xs: 14, md: 15 },
              lineHeight: 1.35,
              minHeight: "2.7em",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {title}
            {artwork.year ? (
              <Typography component="span" color="text.secondary" sx={{ fontSize: "inherit" }}>
                {`, ${artwork.year}`}
              </Typography>
            ) : null}
          </Typography>
        </Box>
      </Box>

      {badges.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            display: "flex",
            gap: 0.5,
            pointerEvents: "none",
          }}
        >
          {badges.map((badge) => (
            <Box
              key={badge}
              component="span"
              sx={(theme) => ({
                px: 1,
                py: 0.25,
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".02em",
                lineHeight: 1.6,
                color: badge === "new" ? "#fff" : theme.tokens.ink,
                bgcolor: badge === "new" ? theme.tokens.ink : "rgba(255,255,255,.92)",
                ...(theme.palette.mode === "dark" && badge === "new"
                  ? { color: "#16232B", bgcolor: "#F2EEE8" }
                  : null),
                ...(theme.palette.mode === "dark" && badge === "popular"
                  ? { color: "#16232B" }
                  : null),
                boxShadow: "0 1px 3px rgba(0,0,0,.12)",
              })}
            >
              {t(badge === "new" ? "badgeNew" : "badgePopular")}
            </Box>
          ))}
        </Box>
      )}

      {onToggleSaved && (
        <IconButton
          onClick={onToggleSaved}
          aria-pressed={saved}
          aria-label={`${t(saved ? "wishlistRemove" : "wishlistAdd")}: ${title}`}
          size="small"
          sx={(theme) => ({
            position: "absolute",
            top: 6,
            right: 6,
            width: 36,
            height: 36,
            bgcolor: "rgba(255,255,255,.88)",
            color: saved ? theme.tokens.action : "#16232B",
            boxShadow: "0 1px 4px rgba(0,0,0,.14)",
            "&:hover": { bgcolor: "#fff" },
          })}
        >
          {saved ? <FavoriteIcon sx={{ fontSize: 20 }} /> : <FavoriteBorderIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      )}

      {/* What it costs, per material, and how big it comes. Two lines are
          always reserved, so a work sold on one material only still lines
          up with its neighbours. */}
      <Box sx={{ mt: 0.75, minHeight: "2.9em", fontSize: { xs: 13, md: 14 } }} aria-live="off">
        {(offers ?? []).map((offer) => (
          <Box
            key={offer.label}
            sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, lineHeight: 1.45 }}
          >
            <Typography component="span" noWrap color="text.secondary" sx={{ fontSize: "inherit", minWidth: 0 }}>
              {offer.label}
            </Typography>
            <Typography component="span" sx={{ fontSize: "inherit", fontWeight: 700, whiteSpace: "nowrap" }}>
              {offer.price}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ display: "block", mt: 0.25, minHeight: "1.66em" }}
      >
        {sizeLabel ?? ""}
      </Typography>

      {onQuickAdd && (
        <Box sx={{ mt: "auto", pt: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onQuickAdd(artwork)}
            aria-label={`${t("quickAddFor")}: ${title}`}
            aria-haspopup="dialog"
            startIcon={<AddShoppingCartIcon sx={{ fontSize: 19 }} />}
            sx={{
              height: 38,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 14,
              whiteSpace: "nowrap",
            }}
          >
            {t("quickAdd")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
